import { BaseEditDialog } from '@features/user-account/components/BaseEditDialog';
import { StyledIconButton } from '@features/user-account/components/StyledIconButton';
import { useAdminApi } from '@hooks/useAdminApi';
import { useOrganizationProducts } from '@hooks/useOrganizationProducts';
import { Box, Stack, Typography } from '@mui/material';
import { Organization, OrganizationTradeLimit } from '@protos/organization';
import { Product } from '@protos/product';
import { TextField } from '@shared/components/TextField';
import { TextInputReadOnlyBox } from '@shared/components/TextInputReadOnlyBox';
import { TextInputSelectBox } from '@shared/components/TextFieldSelectBox';
import { apiErrorMessage } from '@utils/apiError';
import { useEffect, useMemo, useState } from 'react';

interface SetCeilingDialogProps {
  organization: Organization;
  limit?: OrganizationTradeLimit;
  // Every limit the organization already has. The PATCH is an upsert, so without these
  // the Add flow would offer a limited product and silently rewrite its ceiling
  existing: OrganizationTradeLimit[];
  linksVersion: number;
  isOpen: boolean;
  onClose: () => void;
  onLimitSaved: () => void;
}

type CeilingEntry = { symbol: string; ceilingLots: string };

const NumberRegex = new RegExp('^[0-9]*$');

const optionLabel = (product: Product) => `${product.symbol.toLocaleUpperCase()} - ${product.full_name}`;

export const SetCeilingDialog: React.FC<SetCeilingDialogProps> = ({ organization, limit, existing, linksVersion, isOpen, onClose, onLimitSaved }) => {
  const { apiClient } = useAdminApi();
  const { products, loading: productsLoading } = useOrganizationProducts(organization.id, linksVersion);
  const [entries, setEntries] = useState<CeilingEntry[]>([]);

  useEffect(() => {
    setEntries(limit ? [{ symbol: limit.product_symbol, ceilingLots: limit.ceiling_lots.toString() }] : []);
  }, [limit, isOpen]);

  // Adding is only ever a new row. Lowering a ceiling clamps the allocation down at once,
  // so offering a product that already has one turns "Add" into a silent edit.
  const limited = useMemo(() => new Set(existing.map(row => row.product_symbol)), [existing]);
  const selectable = useMemo(() => (limit ? products : products.filter(product => !limited.has(product.symbol))), [products, limited, limit]);

  const taken = useMemo(() => entries.map(entry => entry.symbol), [entries]);
  const unused = useMemo(() => selectable.filter(product => !taken.includes(product.symbol)), [selectable, taken]);

  const labelFor = (symbol: string) => {
    const found = products.find(product => product.symbol === symbol);
    return found ? optionLabel(found) : '';
  };

  const optionsFor = (symbol: string) => {
    const own = selectable.filter(product => product.symbol === symbol);
    return [...own, ...unused].map(optionLabel);
  };

  const onEntryAdd = () => {
    if (unused.length) setEntries([...entries, { symbol: unused[0].symbol, ceilingLots: '0' }]);
  };

  const onEntryRemove = (index: number) => {
    const updated = [...entries];
    updated.splice(index, 1);
    setEntries(updated);
  };

  const onEntryUpdate = (index: number, changes: Partial<CeilingEntry>) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], ...changes };
    setEntries(updated);
  };

  const onProductChange = (index: number, label: string) => {
    const found = products.find(product => optionLabel(product) === label);
    if (found) onEntryUpdate(index, { symbol: found.symbol });
  };

  // One PATCH per product: the API has no bulk write, so this is not atomic. Apply
  // what we can, refresh the grid either way, and name the ones that did not land.
  const onSave = async () => {
    if (!apiClient) return;
    const rows = entries.filter(entry => !!entry.symbol);
    if (!rows.length) throw new Error('Pick at least one product');
    if (rows.some(row => row.ceilingLots === '')) throw new Error('Every product needs a ceiling. Enter 0 explicitly to cap it at zero');

    const failures: { symbol: string; message: string }[] = [];
    let applied = 0;
    for (const row of rows) {
      try {
        await apiClient.setOrganizationTradeLimitCeiling(organization.id, row.symbol, { ceiling_lots: Number(row.ceilingLots) });
        applied += 1;
      } catch (e) {
        failures.push({ symbol: row.symbol, message: apiErrorMessage(e, 'failed') });
      }
    }

    // The reload feeds a new `existing` back in, which drops the saved products from
    // `selectable`. Their rows have to go with them or they render with no product.
    const failed = new Set(failures.map(failure => failure.symbol));
    setEntries(entries.filter(entry => failed.has(entry.symbol)));
    onLimitSaved();
    if (failures.length) {
      const named = failures.map(failure => `${failure.symbol.toLocaleUpperCase()} (${failure.message})`).join(', ');
      throw new Error(`${applied} of ${rows.length} saved. Not saved: ${named}`);
    }
  };

  return (
    <BaseEditDialog
      title={limit ? 'Set Ceiling' : 'Add Trade Limits'}
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[entries]}
      defaultErrorMessage="There was an error setting the ceiling"
      onSave={onSave}
    >
      <Box display="flex" flexDirection="column" marginRight={2} gap={1} minWidth="460px">
        <Typography fontSize={11}>
          {limit
            ? "Raising the ceiling leaves the allocation alone - the new room is the agent's to hand out. Lowering it clamps the allocation down straight away, and is refused if the new ceiling would sit below lots the organization already holds."
            : 'Each product gets its own ceiling. A new limit starts with nothing allocated, so the organization cannot trade on that product until its agent allocates against the ceiling.'}
        </Typography>

        {/* The symbol keys the row, so on an existing limit it is shown, not edited */}
        {limit ? (
          <>
            <TextInputReadOnlyBox label="Product" value={limit.product_symbol.toLocaleUpperCase()} sx={{ width: '360px' }} />
            <TextField
              label="Ceiling Lots"
              value={entries[0]?.ceilingLots ?? '0'}
              sx={{ width: '160px' }}
              error={entries[0]?.ceilingLots === ''}
              onChange={(value: string) => {
                if (NumberRegex.test(value)) onEntryUpdate(0, { ceilingLots: value });
              }}
            />
          </>
        ) : productsLoading ? (
          <Typography fontSize={11}>Loading products...</Typography>
        ) : !products.length ? (
          <Typography fontSize={11} color="error">
            No products to limit. A limit is only consulted for products an effective link covers, so link this organization as a client first.
          </Typography>
        ) : !selectable.length ? (
          <Typography fontSize={11} color="error">
            Every product in scope already has a limit. Set its ceiling from the row instead, so an existing allocation is not rewritten.
          </Typography>
        ) : (
          <Stack direction="column" alignItems="flex-start">
            {/* Keyed by symbol, not index: the select box seeds from defaultValue, so
                removing a row must remount the rest rather than shift them */}
            {entries.map((entry, index) => (
              <Stack key={`ceiling_entry_${entry.symbol}`} direction="row" alignItems="center">
                <TextInputSelectBox
                  label="Product"
                  value={labelFor(entry.symbol)}
                  sx={{ width: '300px' }}
                  onChange={(value: string) => onProductChange(index, value)}
                  options={optionsFor(entry.symbol)}
                />
                <TextField
                  label="Ceiling Lots"
                  value={entry.ceilingLots}
                  sx={{ width: '130px' }}
                  error={entry.ceilingLots === ''}
                  onChange={(value: string) => {
                    if (NumberRegex.test(value)) onEntryUpdate(index, { ceilingLots: value });
                  }}
                />
                <StyledIconButton onClick={() => onEntryRemove(index)} size={'small'} style={{ marginLeft: '5px' }}>
                  <i className={'ri-delete-bin-line'} />
                </StyledIconButton>
              </Stack>
            ))}
            <StyledIconButton onClick={onEntryAdd} disabled={!unused.length}>
              <i className={'ri-add-line'} />
            </StyledIconButton>
          </Stack>
        )}
      </Box>
    </BaseEditDialog>
  );
};
